import { Handle, Position, useNodeConnections } from '@xyflow/react'
import { selectedBorder } from './selectedBorder.js'
import { nodeControlClass } from '../../lib/nodeControlClass.js'
import { fieldNumber } from '../../lib/fieldNumber.js'

// One blend-time row: an input pin plus a typed value. Wired, the wire drives
// the time (in seconds) and the box hides — GraphEvaluator reads the pin first.
function BlendTimeRow({ nodeId, pinId, label, value, onChange, marginBottom }) {
  const wired = useNodeConnections({ id: nodeId, handleType: 'target', handleId: pinId }).length > 0
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom, position: 'relative' }}>
      <Handle type="target" position={Position.Left} id={pinId}
        style={{ background: '#60a0d0', width: 8, height: 8, top: '50%', transform: 'translateY(-50%)' }} />
      <span style={{ marginLeft: 12, color: '#80e880', fontSize: 10, flex: 1 }}>{label}</span>
      {wired ? (
        <span style={{ color: '#60a0d0', fontSize: 10, fontStyle: 'italic' }}>← wired</span>
      ) : (
        <input className={nodeControlClass()}
          type="number"
          min={0} max={5} step={0.05}
          value={value}
          onChange={e => onChange(fieldNumber(e.target.value))}
          onMouseDown={e => e.stopPropagation()}
          style={{
            width: 52, background: '#0a180a', border: '1px solid #1a401a',
            color: '#80e880', padding: '2px 4px', borderRadius: 3,
            fontSize: 10, fontFamily: 'var(--m-mono)', outline: 'none', textAlign: 'right',
          }}
        />
      )}
    </div>
  )
}

export function BlendByBoolNode({ id, data, selected }) {
  const active = !!(data.defaultValue)
  const hasVar = !!data.boolVariable

  const trueBlendTime  = data.trueBlendTime  ?? 0.1
  const falseBlendTime = data.falseBlendTime ?? 0.1

  return (
    <div style={{
      background: selected ? '#1a2a1a' : '#121a12',
      border: `2px solid ${selected ? selectedBorder('#305030') : '#305030'}`,
      borderRadius: 6,
      padding: '10px 14px',
      minWidth: 190,
      color: '#ddd',
      fontFamily: 'var(--m-font)',
      fontSize: 12,
    }}>
      {/* Title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <span style={{ fontSize: 14 }}>🔀</span>
        <span style={{ color: '#80e880', fontWeight: 'bold', fontSize: 11 }}>BLEND BY BOOL</span>
      </div>

      {/* True Pose (B) — plays when true */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6, position: 'relative' }}>
        <Handle type="target" position={Position.Left} id="b"
          style={{ background: '#e07040', width: 10, height: 10, top: '50%', transform: 'translateY(-50%)' }} />
        <span style={{ marginLeft: 14, color: active ? '#e07040' : '#555', fontSize: 11 }}>True Pose</span>
      </div>

      {/* False Pose (A) — plays when false */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10, position: 'relative' }}>
        <Handle type="target" position={Position.Left} id="a"
          style={{ background: '#e07040', width: 10, height: 10, top: '50%', transform: 'translateY(-50%)' }} />
        <span style={{ marginLeft: 14, color: active ? '#555' : '#e07040', fontSize: 11 }}>False Pose</span>
      </div>

      {/* Blend times — wire a value (e.g. Get Variable) into the pin, or type one */}
      <BlendTimeRow nodeId={id} pinId="trueBlendTime-in" label="True Blend Time"
        value={trueBlendTime} onChange={v => data.onChange?.('trueBlendTime', v)} marginBottom={4} />
      <BlendTimeRow nodeId={id} pinId="falseBlendTime-in" label="False Blend Time"
        value={falseBlendTime} onChange={v => data.onChange?.('falseBlendTime', v)} marginBottom={8} />

      {/* Bool input + fallback toggle */}
      <div style={{ marginBottom: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, position: 'relative' }}>
          <Handle type="target" position={Position.Left} id="bool-in"
            style={{ background: '#80e880', width: 8, height: 8, top: '50%', transform: 'translateY(-50%)' }} />
          <span style={{ marginLeft: 12, color: '#80e880', fontSize: 10 }}>
            Active Value{hasVar ? ' (var)' : ''}
          </span>
          <input className={nodeControlClass()}
            type="checkbox"
            checked={active}
            onChange={e => data.onChange?.('defaultValue', e.target.checked)}
            onMouseDown={e => e.stopPropagation()}
            style={{ marginLeft: 'auto', accentColor: '#80e880', cursor: 'pointer', width: 14, height: 14 }}
          />
        </div>
      </div>

      {/* Optional variable override */}
      <div style={{ marginBottom: 4 }}>
        <div style={{ color: '#284028', fontSize: 9, marginBottom: 2 }}>Variable (optional)</div>
        <input className={nodeControlClass()}
          type="text"
          value={data.boolVariable ?? ''}
          onChange={e => data.onChange?.('boolVariable', e.target.value || undefined)}
          onMouseDown={e => e.stopPropagation()}
          placeholder="none"
          style={{
            width: '100%', boxSizing: 'border-box',
            background: '#0a180a', border: '1px solid #1a301a',
            color: hasVar ? '#80e880' : '#555', padding: '3px 6px',
            borderRadius: 3, fontSize: 10,
            fontFamily: 'var(--m-mono)', outline: 'none',
          }}
        />
      </div>

      {/* Output */}
      <Handle type="source" position={Position.Right} id="anim"
        style={{ background: '#e07040', width: 10, height: 10 }} />
    </div>
  )
}
